/**
 * The widget's own fixed copy, by language.
 *
 * What a customer can change (greeting, phrases, subtitle, welcome, error
 * message) arrives from Drupal already resolved. This is only the interface
 * text nobody configures — and the last resort for a language Drupal has no
 * text for. Unknown languages fall back to English.
 */
const COPY = {
  en: {
    actionConfirm: "Confirm",
    actionChange: "Change",
    actionCancel: "Cancel",
    actionSummary: "Check your request",
    actionOptions: "Choose an option",
    createTicket: "Create a support ticket",
    ticketIntro: "Tell us what is happening. We attach this conversation to the ticket, and you get an email when we reply.",
    ticketCreated: "Ticket #{id} created. We will email you when we reply.",
    seeTicket: "See the ticket",
    couldNotCreateTicket: "The ticket could not be created. Try again in a moment.",
    poweredBy: 'Powered by',
    openChat: 'Open chat with {name}',
    hideGreeting: 'Hide greeting',
    closeChat: 'Close chat',
    chatWith: 'Chat with {name}',
    today: 'Today',
    message: 'Message',
    placeholder: 'Write a message…',
    send: 'Send',
    sendWaiting: 'Send (waiting for the reply)',
    typing: 'The assistant is typing…',
    notSent: 'Not sent',
    retry: 'Retry',
    error: "We couldn't send your message. Check your connection and try again.",
    handoff: 'A human advisor already owns this conversation.',
    disclaimer: 'AI-generated answers · may contain mistakes',
    report: 'Report',
    reportTitle: 'Report this chatbot',
    tooLong: '{count} / {max}',
    subtitle: 'Virtual assistant',
    newConversation: 'New conversation',
    resumed: 'Picking up your conversation',
    talkToPerson: "Talk to a person",
    offerPerson: "Would you like to talk to a person?",
    connecting: "Connecting you with a person…",
    inLine: "You are number {n} in line. Someone will be with you shortly.",
    joined: "{name} joined the conversation",
    withPerson: "You are talking with {name}",
    endChat: "End chat",
    endedByYou: "You ended the conversation.",
    endedByAdvisor: "{name} ended the conversation.",
    endedIdle: "The conversation ended after a while without messages.",
    endedLeft: "The conversation ended.",
    endedNoAdvisor: "Nobody could attend you this time.",
    backWithBot: "You are back with the virtual assistant.",
    chooseLanguage: "Right now we can attend you in:",
    continueIn: "Continue in {language}",
    leaveMessage: "Leave a message",
    leaveIntro: "Leave your message and we will reply to your email.",
    yourName: "Your name",
    yourEmail: "Your email",
    yourMessage: "Your message",
    sendMessage: "Send message",
    messageSent: "Thanks! We received your message and will reply to {email}. Meanwhile, you can keep asking the assistant.",
    couldNotConnect: "We could not connect you right now. Try again in a moment.",
    tooMany: "You sent many messages in a short time. Wait a moment and try again.",
    tooManyMessages: "You already left several messages. Try again later.",
    emailCannotReceive: "We can't send email to this address. Check it for typos.",
    reconnecting: "Reconnecting…",
    stillThere: "Are you still there? The conversation ends soon if nobody writes.",
    personDisclaimer: "You are talking with a person",
    offerAsked: "I'll connect you with a person.",
    usuallyFast: "Someone usually replies in under a minute.",
    keepChatting: "Keep chatting with the assistant",
    whatsappAction: "WhatsApp",
    whatsappOpen: "Write to us on WhatsApp",
    whatsappGreeting: "Hi, I'm writing from {site}.",
    chooseOption: "Choose…",
    fieldInvalid: "Check this field.",
    optionalField: "{label} (optional)",
    teamOffline: "Our team isn't connected right now.",
    outsideHours: "We're outside our support hours. We're back {when}.",
    outsideHoursNoDate: "We're outside our support hours.",
    hoursAre: "Our hours, in your time: {hours}.",
    allDay: '24 hours',
    everyDay: 'every day, {hours}',
    todayAt: "today at {time}",
    tomorrowAt: "tomorrow at {time}",
    dayAt: "{day} at {time}",
    leaveInstead: "Leave a message instead",
    personTyping: "{name} is writing",
    unreadOne: "Open chat with {name}, 1 new message",
    unreadMany: "Open chat with {name}, {count} new messages",
    newMessageFrom: "{name} wrote to you",
    emailOffer: "Email me the replies if I close the page",
    emailOfferHint: "We only use it to send you the replies you miss in this conversation.",
    emailSave: "Save",
    emailCancel: "Cancel",
    emailSaved: "If you close the page, we'll email you the replies you miss.",
    soundToggle: "Sound for new messages",
    soundMute: "Mute",
    soundUnmute: "Turn sound on",
    emailRemove: "Remove email",
    emailInvalid: "Check the email address.",
    conversationLog: "Conversation",
    advisorSlow: "{name} is taking longer than usual.",
    advisorAway: "{name} lost their connection. If they don't come back shortly, we'll connect you with someone else on the team.",
    advisorBack: "{name} is back.",
    advisorLeft: "{name} is no longer available. We're connecting you with someone else on the team…",
    advisorSlowHint: "You can keep waiting, or leave a message and we will reply by email.",
    keepWaiting: "Keep waiting",
    consentTitle: "Authorization to use the chat",
    consentAccept: "I agree",
    consentDecline: "I don't agree",
    consentDeclined: "Without your authorization the chat isn't available.",
    consentChange: "Change my decision",
    consentMenu: "Your authorization",
    // Short labels for the header row; the full wording is the tooltip.
    consentAction: "Authorization",
    personAction: "Live agent",
    newAction: "New chat",
    consentGiven: "You gave your authorization on {date}.",
    consentId: "Authorization ID: {id}",
    consentWithdraw: "Withdraw authorization",
    consentWithdrawn: "You withdrew your authorization (ID {id}); keep this ID if you ever need proof. To chat again, authorize it below.",
    consentWithdrawFailed: "We couldn't withdraw your authorization. Try again in a moment.",
    consentAgain: "To continue, please authorize the use of the chat again.",
    micStart: "Dictate your message",
    micRecording: "Recording",
    micLeft: "{seconds} s left",
    micTenLeft: "10 seconds left",
    micCancel: "Cancel recording",
    micDone: "Done",
    micTranscribing: "Transcribing…",
    micDenied: "We can't use your microphone. Allow it in your browser, or type your question.",
    micNothing: "We didn't catch that. Try again closer to the microphone, or type your question.",
    micLimit: "You dictated many messages in a short time. Wait a moment, or type your question.",
    micError: "Dictation isn't available right now. Please type your question.",
    voteGroup: "Was this answer helpful?",
    voteUp: "Helpful",
    voteDown: "Not helpful",
    voteThanks: "Thanks for your feedback.",
    voteFailed: "We couldn't save your vote. Try again in a moment.",
  },
  es: {
    actionConfirm: "Confirmar",
    actionChange: "Cambiar",
    actionCancel: "Cancelar",
    actionSummary: "Revisa tu solicitud",
    actionOptions: "Elige una opción",
    createTicket: "Crear un ticket de soporte",
    ticketIntro: "Cuéntanos qué pasa. Adjuntamos esta conversación al ticket y te avisamos por correo cuando respondamos.",
    ticketCreated: "Ticket #{id} creado. Te avisaremos por correo cuando respondamos.",
    seeTicket: "Ver el ticket",
    couldNotCreateTicket: "No se pudo crear el ticket. Inténtalo de nuevo en un momento.",
    poweredBy: 'Con la tecnología de',
    openChat: 'Abrir chat con {name}',
    hideGreeting: 'Ocultar saludo',
    closeChat: 'Cerrar chat',
    chatWith: 'Chat con {name}',
    today: 'Hoy',
    message: 'Mensaje',
    placeholder: 'Escribe un mensaje…',
    send: 'Enviar',
    sendWaiting: 'Enviar (esperando respuesta)',
    typing: 'El asistente está escribiendo…',
    notSent: 'No enviado',
    retry: 'Reintentar',
    error: 'No pudimos enviar tu mensaje. Revisa tu conexión e intenta de nuevo.',
    handoff: 'Un asesor humano ya está en esta conversación.',
    disclaimer: 'Respuestas generadas con IA · pueden contener errores',
    report: 'Reportar',
    reportTitle: 'Reportar este chatbot',
    tooLong: '{count} / {max}',
    subtitle: 'Asistente virtual',
    newConversation: 'Nueva conversación',
    resumed: 'Retomamos tu conversación',
    talkToPerson: "Hablar con una persona",
    offerPerson: "¿Quieres hablar con una persona?",
    connecting: "Te estamos conectando con una persona…",
    inLine: "Eres el número {n} en la fila. En breve te atienden.",
    joined: "{name} se unió a la conversación",
    withPerson: "Estás hablando con {name}",
    endChat: "Terminar chat",
    endedByYou: "Terminaste la conversación.",
    endedByAdvisor: "{name} terminó la conversación.",
    endedIdle: "La conversación terminó después de un rato sin mensajes.",
    endedLeft: "La conversación terminó.",
    endedNoAdvisor: "Esta vez nadie pudo atenderte.",
    backWithBot: "Volviste con el asistente virtual.",
    chooseLanguage: "Ahora te podemos atender en:",
    continueIn: "Seguir en {language}",
    leaveMessage: "Dejar un mensaje",
    leaveIntro: "Déjanos tu mensaje y te respondemos a tu email.",
    yourName: "Tu nombre",
    yourEmail: "Tu email",
    yourMessage: "Tu mensaje",
    sendMessage: "Enviar mensaje",
    messageSent: "¡Gracias! Recibimos tu mensaje y te responderemos a {email}. Mientras tanto, puedes seguir preguntándole al asistente.",
    couldNotConnect: "No pudimos conectarte ahora. Inténtalo en un momento.",
    tooMany: "Enviaste muchos mensajes en poco tiempo. Espera un momento e inténtalo de nuevo.",
    tooManyMessages: "Ya dejaste varios mensajes. Inténtalo más tarde.",
    emailCannotReceive: "No podemos enviar correos a esta dirección. Revisa que esté bien escrita.",
    reconnecting: "Reconectando…",
    stillThere: "¿Sigues ahí? La conversación termina pronto si nadie escribe.",
    personDisclaimer: "Estás hablando con una persona",
    offerAsked: "Te comunico con una persona.",
    usuallyFast: "Normalmente alguien responde en menos de un minuto.",
    keepChatting: "Seguir con el asistente",
    whatsappAction: "WhatsApp",
    whatsappOpen: "Escríbenos por WhatsApp",
    whatsappGreeting: "Hola, les escribo desde {site}.",
    chooseOption: "Elige…",
    fieldInvalid: "Revisa este campo.",
    optionalField: "{label} (opcional)",
    teamOffline: "Nuestro equipo no está conectado en este momento.",
    outsideHours: "Estamos fuera de nuestro horario de atención. Volvemos {when}.",
    outsideHoursNoDate: "Estamos fuera de nuestro horario de atención.",
    hoursAre: "Nuestro horario, en tu hora: {hours}.",
    allDay: '24 horas',
    everyDay: 'todos los días, {hours}',
    todayAt: "hoy a las {time}",
    tomorrowAt: "mañana a las {time}",
    dayAt: "el {day} a las {time}",
    leaveInstead: "Mejor dejar un mensaje",
    personTyping: "{name} está escribiendo",
    unreadOne: "Abrir chat con {name}, 1 mensaje nuevo",
    unreadMany: "Abrir chat con {name}, {count} mensajes nuevos",
    newMessageFrom: "{name} te escribió",
    emailOffer: "Enviarme las respuestas por email si cierro la página",
    emailOfferHint: "Solo lo usamos para enviarte las respuestas que te pierdas de esta conversación.",
    emailSave: "Guardar",
    emailCancel: "Cancelar",
    emailSaved: "Si cierras la página, te enviaremos por email las respuestas que te pierdas.",
    soundToggle: "Sonido de mensajes nuevos",
    soundMute: "Silenciar",
    soundUnmute: "Activar sonido",
    emailRemove: "Quitar email",
    emailInvalid: "Revisa la dirección de email.",
    conversationLog: "Conversación",
    advisorSlow: "{name} está tardando más de lo normal.",
    advisorAway: "{name} perdió la conexión. Si no vuelve en un momento, te conectamos con otra persona del equipo.",
    advisorBack: "{name} volvió.",
    advisorLeft: "{name} ya no está disponible. Te estamos conectando con otra persona del equipo…",
    advisorSlowHint: "Puedes seguir esperando, o dejar un mensaje y te respondemos por email.",
    keepWaiting: "Seguir esperando",
    consentTitle: "Autorización para usar el chat",
    consentAccept: "Sí autorizo",
    consentDecline: "No autorizo",
    consentDeclined: "Sin tu autorización el chat no está disponible.",
    consentChange: "Cambiar mi decisión",
    consentMenu: "Tu autorización",
    consentAction: "Autorización",
    personAction: "Asesor",
    newAction: "Nuevo chat",
    consentGiven: "Diste tu autorización el {date}.",
    consentId: "ID de la autorización: {id}",
    consentWithdraw: "Retirar autorización",
    consentWithdrawn: "Retiraste tu autorización (ID {id}); guarda este ID por si necesitas la prueba. Para volver a chatear, autorízala aquí abajo.",
    consentWithdrawFailed: "No pudimos retirar tu autorización. Inténtalo de nuevo en un momento.",
    consentAgain: "Para continuar, vuelve a autorizar el uso del chat.",
    micStart: "Dictar tu mensaje",
    micRecording: "Grabando",
    micLeft: "quedan {seconds} s",
    micTenLeft: "Quedan 10 segundos",
    micCancel: "Cancelar la grabación",
    micDone: "Listo",
    micTranscribing: "Transcribiendo…",
    micDenied: "No podemos usar tu micrófono. Permítelo en tu navegador o escribe tu pregunta.",
    micNothing: "No te entendimos. Inténtalo más cerca del micrófono o escribe tu pregunta.",
    micLimit: "Dictaste muchos mensajes en poco tiempo. Espera un momento o escribe tu pregunta.",
    micError: "El dictado no está disponible ahora. Escribe tu pregunta, por favor.",
    voteGroup: "¿Te sirvió esta respuesta?",
    voteUp: "Me sirvió",
    voteDown: "No me sirvió",
    voteThanks: "Gracias por tu opinión.",
    voteFailed: "No pudimos guardar tu voto. Inténtalo en un momento.",
  },
  pt: {
    actionConfirm: "Confirmar",
    actionChange: "Alterar",
    actionCancel: "Cancelar",
    actionSummary: "Confira a sua solicitação",
    actionOptions: "Escolha uma opção",
    createTicket: "Criar um ticket de suporte",
    ticketIntro: "Conte-nos o que está acontecendo. Anexamos esta conversa ao ticket e avisamos por e-mail quando respondermos.",
    ticketCreated: "Ticket #{id} criado. Avisaremos por e-mail quando respondermos.",
    seeTicket: "Ver o ticket",
    couldNotCreateTicket: "Não foi possível criar o ticket. Tente novamente em instantes.",
    poweredBy: 'Com a tecnologia de',
    openChat: 'Abrir chat com {name}',
    hideGreeting: 'Ocultar saudação',
    closeChat: 'Fechar chat',
    chatWith: 'Chat com {name}',
    today: 'Hoje',
    message: 'Mensagem',
    placeholder: 'Escreva uma mensagem…',
    send: 'Enviar',
    sendWaiting: 'Enviar (aguardando resposta)',
    typing: 'O assistente está digitando…',
    notSent: 'Não enviada',
    retry: 'Tentar de novo',
    error: 'Não conseguimos enviar sua mensagem. Verifique sua conexão e tente de novo.',
    handoff: 'Um atendente humano já está nesta conversa.',
    disclaimer: 'Respostas geradas por IA · podem conter erros',
    report: 'Denunciar',
    reportTitle: 'Denunciar este chatbot',
    tooLong: '{count} / {max}',
    subtitle: 'Assistente virtual',
    newConversation: 'Nova conversa',
    resumed: 'Retomamos sua conversa',
    talkToPerson: "Falar com uma pessoa",
    offerPerson: "Quer falar com uma pessoa?",
    connecting: "Conectando você com uma pessoa…",
    inLine: "Você é o número {n} na fila. Em breve alguém vai atender.",
    joined: "{name} entrou na conversa",
    withPerson: "Você está falando com {name}",
    endChat: "Encerrar chat",
    endedByYou: "Você encerrou a conversa.",
    endedByAdvisor: "{name} encerrou a conversa.",
    endedIdle: "A conversa terminou após um tempo sem mensagens.",
    endedLeft: "A conversa terminou.",
    endedNoAdvisor: "Desta vez ninguém pôde atender você.",
    backWithBot: "Você voltou ao assistente virtual.",
    chooseLanguage: "Agora podemos atender você em:",
    continueIn: "Continuar em {language}",
    leaveMessage: "Deixar uma mensagem",
    leaveIntro: "Deixe sua mensagem e responderemos para o seu e-mail.",
    yourName: "Seu nome",
    yourEmail: "Seu e-mail",
    yourMessage: "Sua mensagem",
    sendMessage: "Enviar mensagem",
    messageSent: "Obrigado! Recebemos sua mensagem e vamos responder para {email}. Enquanto isso, você pode continuar perguntando ao assistente.",
    couldNotConnect: "Não foi possível conectar agora. Tente em um momento.",
    tooMany: "Você enviou muitas mensagens em pouco tempo. Aguarde um momento e tente de novo.",
    tooManyMessages: "Você já deixou várias mensagens. Tente mais tarde.",
    emailCannotReceive: "Não conseguimos enviar e-mails para este endereço. Verifique se está escrito corretamente.",
    reconnecting: "Reconectando…",
    stillThere: "Ainda está aí? A conversa termina em breve se ninguém escrever.",
    personDisclaimer: "Você está falando com uma pessoa",
    offerAsked: "Vou conectar você com uma pessoa.",
    usuallyFast: "Normalmente alguém responde em menos de um minuto.",
    keepChatting: "Continuar com o assistente",
    whatsappAction: "WhatsApp",
    whatsappOpen: "Fale conosco no WhatsApp",
    whatsappGreeting: "Olá, estou escrevendo do site {site}.",
    chooseOption: "Escolha…",
    fieldInvalid: "Confira este campo.",
    optionalField: "{label} (opcional)",
    teamOffline: "Nossa equipe não está conectada agora.",
    outsideHours: "Estamos fora do nosso horário de atendimento. Voltamos {when}.",
    outsideHoursNoDate: "Estamos fora do nosso horário de atendimento.",
    hoursAre: "Nosso horário, no seu fuso: {hours}.",
    allDay: '24 horas',
    everyDay: 'todos os dias, {hours}',
    todayAt: "hoje às {time}",
    tomorrowAt: "amanhã às {time}",
    dayAt: "{day} às {time}",
    leaveInstead: "Prefiro deixar uma mensagem",
    personTyping: "{name} está escrevendo",
    unreadOne: "Abrir chat com {name}, 1 mensagem nova",
    unreadMany: "Abrir chat com {name}, {count} mensagens novas",
    newMessageFrom: "{name} escreveu para você",
    emailOffer: "Receber as respostas por e-mail se eu fechar a página",
    emailOfferHint: "Só usamos para enviar as respostas que você perder nesta conversa.",
    emailSave: "Salvar",
    emailCancel: "Cancelar",
    emailSaved: "Se você fechar a página, enviaremos por e-mail as respostas que você perder.",
    soundToggle: "Som de mensagens novas",
    soundMute: "Silenciar",
    soundUnmute: "Ativar som",
    emailRemove: "Remover e-mail",
    emailInvalid: "Verifique o endereço de e-mail.",
    conversationLog: "Conversa",
    advisorSlow: "{name} está demorando mais que o normal.",
    advisorAway: "{name} perdeu a conexão. Se não voltar em instantes, conectamos você com outra pessoa da equipe.",
    advisorBack: "{name} voltou.",
    advisorLeft: "{name} não está mais disponível. Estamos conectando você com outra pessoa da equipe…",
    advisorSlowHint: "Você pode continuar esperando ou deixar uma mensagem e responderemos por e-mail.",
    keepWaiting: "Continuar esperando",
    consentTitle: "Autorização para usar o chat",
    consentAccept: "Eu autorizo",
    consentDecline: "Não autorizo",
    consentDeclined: "Sem a sua autorização o chat não está disponível.",
    consentChange: "Mudar minha decisão",
    consentMenu: "Sua autorização",
    consentAction: "Autorização",
    personAction: "Atendente",
    newAction: "Novo chat",
    consentGiven: "Você deu sua autorização em {date}.",
    consentId: "ID da autorização: {id}",
    consentWithdraw: "Retirar autorização",
    consentWithdrawn: "Você retirou sua autorização (ID {id}); guarde este ID caso precise da prova. Para conversar de novo, autorize aqui embaixo.",
    consentWithdrawFailed: "Não foi possível retirar sua autorização. Tente de novo em instantes.",
    consentAgain: "Para continuar, autorize de novo o uso do chat.",
    micStart: "Ditar sua mensagem",
    micRecording: "Gravando",
    micLeft: "faltam {seconds} s",
    micTenLeft: "Faltam 10 segundos",
    micCancel: "Cancelar a gravação",
    micDone: "Pronto",
    micTranscribing: "Transcrevendo…",
    micDenied: "Não conseguimos usar seu microfone. Permita no navegador ou digite sua pergunta.",
    micNothing: "Não entendemos. Tente mais perto do microfone ou digite sua pergunta.",
    micLimit: "Você ditou muitas mensagens em pouco tempo. Aguarde um momento ou digite sua pergunta.",
    micError: "O ditado não está disponível agora. Digite sua pergunta, por favor.",
    voteGroup: "Esta resposta ajudou?",
    voteUp: "Ajudou",
    voteDown: "Não ajudou",
    voteThanks: "Obrigado pela sua opinião.",
    voteFailed: "Não conseguimos salvar seu voto. Tente novamente em instantes.",
  },
  fr: {
    actionConfirm: "Confirmer",
    actionChange: "Modifier",
    actionCancel: "Annuler",
    actionSummary: "Vérifiez votre demande",
    actionOptions: "Choisissez une option",
    createTicket: "Créer un ticket d’assistance",
    ticketIntro: "Dites-nous ce qui se passe. Nous joignons cette conversation au ticket et vous prévenons par e-mail dès notre réponse.",
    ticketCreated: "Ticket n°{id} créé. Nous vous préviendrons par e-mail dès notre réponse.",
    seeTicket: "Voir le ticket",
    couldNotCreateTicket: "Le ticket n’a pas pu être créé. Réessayez dans un instant.",
    poweredBy: 'Propulsé par',
    openChat: 'Ouvrir le chat avec {name}',
    hideGreeting: 'Masquer le message',
    closeChat: 'Fermer le chat',
    chatWith: 'Chat avec {name}',
    today: "Aujourd'hui",
    message: 'Message',
    placeholder: 'Écrivez un message…',
    send: 'Envoyer',
    sendWaiting: 'Envoyer (réponse en attente)',
    typing: "L'assistant écrit…",
    notSent: 'Non envoyé',
    retry: 'Réessayer',
    error: "Nous n'avons pas pu envoyer votre message. Vérifiez votre connexion et réessayez.",
    handoff: 'Un conseiller humain a repris cette conversation.',
    disclaimer: "Réponses générées par l'IA · peuvent contenir des erreurs",
    report: 'Signaler',
    reportTitle: 'Signaler ce chatbot',
    tooLong: '{count} / {max}',
    subtitle: 'Assistant virtuel',
    newConversation: 'Nouvelle conversation',
    resumed: 'Nous reprenons votre conversation',
    talkToPerson: "Parler à une personne",
    offerPerson: "Voulez-vous parler à une personne ?",
    connecting: "Nous vous mettons en relation avec une personne…",
    inLine: "Vous êtes le numéro {n} dans la file. Quelqu’un va vous répondre.",
    joined: "{name} a rejoint la conversation",
    withPerson: "Vous parlez avec {name}",
    endChat: "Terminer le chat",
    endedByYou: "Vous avez terminé la conversation.",
    endedByAdvisor: "{name} a terminé la conversation.",
    endedIdle: "La conversation s’est terminée après un moment sans messages.",
    endedLeft: "La conversation est terminée.",
    endedNoAdvisor: "Personne n’a pu vous répondre cette fois.",
    backWithBot: "Vous êtes de retour avec l’assistant virtuel.",
    chooseLanguage: "En ce moment, nous pouvons vous répondre en :",
    continueIn: "Continuer en {language}",
    leaveMessage: "Laisser un message",
    leaveIntro: "Laissez votre message et nous répondrons à votre e-mail.",
    yourName: "Votre nom",
    yourEmail: "Votre e-mail",
    yourMessage: "Votre message",
    sendMessage: "Envoyer le message",
    messageSent: "Merci ! Nous avons reçu votre message et répondrons à {email}. En attendant, vous pouvez continuer à poser vos questions à l’assistant.",
    couldNotConnect: "Impossible de vous connecter pour le moment. Réessayez dans un instant.",
    tooMany: "Vous avez envoyé beaucoup de messages en peu de temps. Patientez un instant et réessayez.",
    tooManyMessages: "Vous avez déjà laissé plusieurs messages. Réessayez plus tard.",
    emailCannotReceive: "Nous ne pouvons pas envoyer d’e-mail à cette adresse. Vérifiez qu’elle est bien écrite.",
    reconnecting: "Reconnexion…",
    stillThere: "Êtes-vous toujours là ? La conversation se termine bientôt si personne n’écrit.",
    personDisclaimer: "Vous parlez avec une personne",
    offerAsked: "Je vous mets en relation avec une personne.",
    usuallyFast: "Quelqu’un répond généralement en moins d’une minute.",
    keepChatting: "Continuer avec l’assistant",
    whatsappAction: "WhatsApp",
    whatsappOpen: "Écrivez-nous sur WhatsApp",
    whatsappGreeting: "Bonjour, je vous écris depuis {site}.",
    chooseOption: "Choisissez…",
    fieldInvalid: "Vérifiez ce champ.",
    optionalField: "{label} (facultatif)",
    teamOffline: "Notre équipe n’est pas connectée pour le moment.",
    outsideHours: "Nous sommes en dehors de nos horaires d’assistance. Retour {when}.",
    outsideHoursNoDate: "Nous sommes en dehors de nos horaires d’assistance.",
    hoursAre: "Nos horaires, à votre heure : {hours}.",
    allDay: '24 h/24',
    everyDay: 'tous les jours, {hours}',
    todayAt: "aujourd’hui à {time}",
    tomorrowAt: "demain à {time}",
    dayAt: "{day} à {time}",
    leaveInstead: "Laisser plutôt un message",
    personTyping: "{name} écrit",
    unreadOne: "Ouvrir le chat avec {name}, 1 nouveau message",
    unreadMany: "Ouvrir le chat avec {name}, {count} nouveaux messages",
    newMessageFrom: "{name} vous a écrit",
    emailOffer: "Recevoir les réponses par e-mail si je ferme la page",
    emailOfferHint: "Nous l’utilisons uniquement pour vous envoyer les réponses que vous manquez dans cette conversation.",
    emailSave: "Enregistrer",
    emailCancel: "Annuler",
    emailSaved: "Si vous fermez la page, nous vous enverrons par e-mail les réponses que vous manquez.",
    soundToggle: "Son des nouveaux messages",
    soundMute: "Couper le son",
    soundUnmute: "Activer le son",
    emailRemove: "Retirer l’e-mail",
    emailInvalid: "Vérifiez l’adresse e-mail.",
    conversationLog: "Conversation",
    advisorSlow: "{name} met plus de temps que d’habitude.",
    advisorAway: "{name} a perdu la connexion. Sans retour rapide, nous vous mettons en relation avec une autre personne de l’équipe.",
    advisorBack: "{name} est de retour.",
    advisorLeft: "{name} n’est plus disponible. Nous vous mettons en relation avec une autre personne de l’équipe…",
    advisorSlowHint: "Vous pouvez continuer à attendre, ou laisser un message et nous répondrons par e-mail.",
    keepWaiting: "Continuer à attendre",
    consentTitle: "Autorisation d’utiliser le chat",
    consentAccept: "J’accepte",
    consentDecline: "Je refuse",
    consentDeclined: "Sans votre autorisation, le chat n’est pas disponible.",
    consentChange: "Changer ma décision",
    consentMenu: "Votre autorisation",
    consentAction: "Autorisation",
    personAction: "Conseiller",
    newAction: "Nouveau chat",
    consentGiven: "Vous avez donné votre autorisation le {date}.",
    consentId: "ID de l’autorisation : {id}",
    consentWithdraw: "Retirer l’autorisation",
    consentWithdrawn: "Vous avez retiré votre autorisation (ID {id}) ; conservez cet ID si vous avez besoin d’une preuve. Pour discuter à nouveau, autorisez-la ci-dessous.",
    consentWithdrawFailed: "Impossible de retirer votre autorisation. Réessayez dans un instant.",
    consentAgain: "Pour continuer, autorisez à nouveau l’utilisation du chat.",
    micStart: "Dicter votre message",
    micRecording: "Enregistrement",
    micLeft: "encore {seconds} s",
    micTenLeft: "Plus que 10 secondes",
    micCancel: "Annuler l’enregistrement",
    micDone: "Terminé",
    micTranscribing: "Transcription…",
    micDenied: "Nous ne pouvons pas utiliser votre micro. Autorisez-le dans votre navigateur ou écrivez votre question.",
    micNothing: "Nous n’avons pas compris. Réessayez plus près du micro ou écrivez votre question.",
    micLimit: "Vous avez dicté beaucoup de messages en peu de temps. Patientez un instant ou écrivez votre question.",
    micError: "La dictée n’est pas disponible pour le moment. Écrivez votre question, s’il vous plaît.",
    voteGroup: "Cette réponse vous a-t-elle aidé ?",
    voteUp: "Utile",
    voteDown: "Pas utile",
    voteThanks: "Merci pour votre avis.",
    voteFailed: "Nous n’avons pas pu enregistrer votre vote. Réessayez dans un instant.",
  },
  de: {
    actionConfirm: "Bestätigen",
    actionChange: "Ändern",
    actionCancel: "Abbrechen",
    actionSummary: "Prüfen Sie Ihre Anfrage",
    actionOptions: "Wählen Sie eine Option",
    createTicket: "Support-Ticket erstellen",
    ticketIntro: "Erzählen Sie uns, was passiert. Wir hängen dieses Gespräch an das Ticket an und benachrichtigen Sie per E-Mail, sobald wir antworten.",
    ticketCreated: "Ticket #{id} erstellt. Wir benachrichtigen Sie per E-Mail, sobald wir antworten.",
    seeTicket: "Ticket ansehen",
    couldNotCreateTicket: "Das Ticket konnte nicht erstellt werden. Versuchen Sie es gleich noch einmal.",
    poweredBy: 'Bereitgestellt von',
    openChat: 'Chat mit {name} öffnen',
    hideGreeting: 'Begrüßung ausblenden',
    closeChat: 'Chat schließen',
    chatWith: 'Chat mit {name}',
    today: 'Heute',
    message: 'Nachricht',
    placeholder: 'Nachricht schreiben…',
    send: 'Senden',
    sendWaiting: 'Senden (Antwort ausstehend)',
    typing: 'Der Assistent schreibt…',
    notSent: 'Nicht gesendet',
    retry: 'Erneut versuchen',
    error: 'Deine Nachricht konnte nicht gesendet werden. Prüfe deine Verbindung und versuche es erneut.',
    handoff: 'Ein Mitarbeiter hat dieses Gespräch übernommen.',
    disclaimer: 'KI-generierte Antworten · können Fehler enthalten',
    report: 'Melden',
    reportTitle: 'Diesen Chatbot melden',
    tooLong: '{count} / {max}',
    subtitle: 'Virtueller Assistent',
    newConversation: 'Neues Gespräch',
    resumed: 'Wir setzen dein Gespräch fort',
    talkToPerson: "Mit einer Person sprechen",
    offerPerson: "Möchten Sie mit einer Person sprechen?",
    connecting: "Wir verbinden Sie mit einer Person…",
    inLine: "Sie sind Nummer {n} in der Warteschlange. Gleich ist jemand für Sie da.",
    joined: "{name} ist dem Gespräch beigetreten",
    withPerson: "Sie sprechen mit {name}",
    endChat: "Chat beenden",
    endedByYou: "Sie haben das Gespräch beendet.",
    endedByAdvisor: "{name} hat das Gespräch beendet.",
    endedIdle: "Das Gespräch endete nach einer Weile ohne Nachrichten.",
    endedLeft: "Das Gespräch ist beendet.",
    endedNoAdvisor: "Diesmal konnte niemand für Sie da sein.",
    backWithBot: "Sie sind wieder beim virtuellen Assistenten.",
    chooseLanguage: "Im Moment können wir Sie betreuen auf:",
    continueIn: "Weiter auf {language}",
    leaveMessage: "Nachricht hinterlassen",
    leaveIntro: "Hinterlassen Sie Ihre Nachricht, wir antworten an Ihre E-Mail-Adresse.",
    yourName: "Ihr Name",
    yourEmail: "Ihre E-Mail",
    yourMessage: "Ihre Nachricht",
    sendMessage: "Nachricht senden",
    messageSent: "Danke! Wir haben Ihre Nachricht erhalten und antworten an {email}. Bis dahin können Sie dem Assistenten weiter Fragen stellen.",
    couldNotConnect: "Wir konnten Sie gerade nicht verbinden. Versuchen Sie es gleich noch einmal.",
    tooMany: "Sie haben in kurzer Zeit viele Nachrichten gesendet. Warten Sie einen Moment und versuchen Sie es erneut.",
    tooManyMessages: "Sie haben bereits mehrere Nachrichten hinterlassen. Versuchen Sie es später noch einmal.",
    emailCannotReceive: "An diese Adresse können wir keine E-Mails senden. Prüfen Sie die Schreibweise.",
    reconnecting: "Neu verbinden…",
    stillThere: "Sind Sie noch da? Das Gespräch endet bald, wenn niemand schreibt.",
    personDisclaimer: "Sie sprechen mit einer Person",
    offerAsked: "Ich verbinde Sie mit einer Person.",
    usuallyFast: "Meist antwortet jemand in weniger als einer Minute.",
    keepChatting: "Weiter mit dem Assistenten",
    whatsappAction: "WhatsApp",
    whatsappOpen: "Schreiben Sie uns auf WhatsApp",
    whatsappGreeting: "Hallo, ich schreibe Ihnen von {site}.",
    chooseOption: "Bitte wählen…",
    fieldInvalid: "Bitte prüfen Sie dieses Feld.",
    optionalField: "{label} (optional)",
    teamOffline: "Unser Team ist gerade nicht verbunden.",
    outsideHours: "Wir sind außerhalb unserer Servicezeiten. Wir sind {when} wieder da.",
    outsideHoursNoDate: "Wir sind außerhalb unserer Servicezeiten.",
    hoursAre: "Unsere Zeiten, in Ihrer Uhrzeit: {hours}.",
    allDay: 'rund um die Uhr',
    everyDay: 'täglich, {hours}',
    todayAt: "heute um {time}",
    tomorrowAt: "morgen um {time}",
    dayAt: "am {day} um {time}",
    leaveInstead: "Lieber eine Nachricht hinterlassen",
    personTyping: "{name} schreibt",
    unreadOne: "Chat mit {name} öffnen, 1 neue Nachricht",
    unreadMany: "Chat mit {name} öffnen, {count} neue Nachrichten",
    newMessageFrom: "{name} hat Ihnen geschrieben",
    emailOffer: "Antworten per E-Mail erhalten, falls ich die Seite schließe",
    emailOfferHint: "Wir verwenden sie nur, um Ihnen Antworten aus diesem Gespräch zu senden, die Sie verpassen.",
    emailSave: "Speichern",
    emailCancel: "Abbrechen",
    emailSaved: "Wenn Sie die Seite schließen, senden wir Ihnen verpasste Antworten per E-Mail.",
    soundToggle: "Ton bei neuen Nachrichten",
    soundMute: "Stummschalten",
    soundUnmute: "Ton einschalten",
    emailRemove: "E-Mail entfernen",
    emailInvalid: "Prüfen Sie die E-Mail-Adresse.",
    conversationLog: "Unterhaltung",
    advisorSlow: "{name} braucht länger als üblich.",
    advisorAway: "{name} hat die Verbindung verloren. Kommt {name} nicht gleich zurück, verbinden wir Sie mit einer anderen Person aus dem Team.",
    advisorBack: "{name} ist wieder da.",
    advisorLeft: "{name} ist nicht mehr verfügbar. Wir verbinden Sie mit einer anderen Person aus dem Team…",
    advisorSlowHint: "Sie können weiter warten oder eine Nachricht hinterlassen, wir antworten per E-Mail.",
    keepWaiting: "Weiter warten",
    consentTitle: "Einwilligung zur Nutzung des Chats",
    consentAccept: "Ich stimme zu",
    consentDecline: "Ich stimme nicht zu",
    consentDeclined: "Ohne Ihre Einwilligung ist der Chat nicht verfügbar.",
    consentChange: "Meine Entscheidung ändern",
    consentMenu: "Ihre Einwilligung",
    consentAction: "Einwilligung",
    personAction: "Berater",
    newAction: "Neuer Chat",
    consentGiven: "Sie haben am {date} eingewilligt.",
    consentId: "ID der Einwilligung: {id}",
    consentWithdraw: "Einwilligung widerrufen",
    consentWithdrawn: "Sie haben Ihre Einwilligung widerrufen (ID {id}); bewahren Sie diese ID auf, falls Sie einen Nachweis brauchen. Um wieder zu chatten, willigen Sie unten ein.",
    consentWithdrawFailed: "Ihre Einwilligung konnte nicht widerrufen werden. Versuchen Sie es gleich noch einmal.",
    consentAgain: "Um fortzufahren, willigen Sie bitte erneut in die Nutzung des Chats ein.",
    micStart: "Nachricht diktieren",
    micRecording: "Aufnahme",
    micLeft: "noch {seconds} s",
    micTenLeft: "Noch 10 Sekunden",
    micCancel: "Aufnahme abbrechen",
    micDone: "Fertig",
    micTranscribing: "Wird transkribiert…",
    micDenied: "Wir können Ihr Mikrofon nicht verwenden. Erlauben Sie es im Browser oder tippen Sie Ihre Frage.",
    micNothing: "Wir haben Sie nicht verstanden. Versuchen Sie es näher am Mikrofon oder tippen Sie Ihre Frage.",
    micLimit: "Sie haben in kurzer Zeit viele Nachrichten diktiert. Warten Sie einen Moment oder tippen Sie Ihre Frage.",
    micError: "Das Diktieren ist gerade nicht verfügbar. Bitte tippen Sie Ihre Frage.",
    voteGroup: "War diese Antwort hilfreich?",
    voteUp: "Hilfreich",
    voteDown: "Nicht hilfreich",
    voteThanks: "Danke für Ihr Feedback.",
    voteFailed: "Ihre Bewertung konnte nicht gespeichert werden. Versuchen Sie es gleich noch einmal.",
  },
};

/** Whether the widget has its own texts for a locale ("pt-br" → pt). */
export function hasCopy(locale) {
  const code = String(locale || '').toLowerCase();
  return Boolean(COPY[code] || COPY[code.slice(0, 2)]);
}

/**
 * A translator for one language: t('openChat', { name }). `extra`: the
 * texts the backend translated for a language the client configured that
 * the widget has none of its own for (ADR-0108) — never over its own five.
 */
export function translator(locale, extra = null) {
  const code = String(locale || '').toLowerCase();
  const own = COPY[code] || COPY[code.slice(0, 2)];
  const table = own || (extra && typeof extra === 'object' ? { ...COPY.en, ...extra } : COPY.en);
  return (key, values = {}) => {
    const text = table[key] ?? COPY.en[key] ?? key;
    return text.replace(/\{(\w+)\}/g, (_, name) => String(values[name] ?? ''));
  };
}

export { COPY };
